<template>
  <div class="context-sidebar">
    <!-- Contact Information -->
    <div class="card">
      <div class="card-header">
        <i class="fa fa-user"></i> Customer Contact
      </div>
      <div class="card-body">
        <div v-if="conversation.context.contactName" class="mb-1">
          <b>{{ conversation.context.contactName }}</b>
        </div>

        <div v-if="conversation.context.contactCompany" class="mb-1 small">
          <b>{{ conversation.context.contactCompany }}</b>
        </div>

        <div v-if="conversation.context.contactEmail" class="mb-1 small">
          <a :href="`mailto:${conversation.context.contactEmail}`"><b>{{ conversation.context.contactEmail }}</b></a>
        </div>

        <div v-if="conversation.context.contactPhone" class="mb-1 small">
          <a :href="`tel:${conversation.context.contactPhone}`"><b>{{ conversation.context.contactPhone }}</b></a>
        </div>
        <div v-if="!hasContactInfo" class="text-muted small">
          <i class="fa fa-info-circle"></i> No contact information available
        </div>
      </div>
    </div>

    <div class="card">
      <div class="card-header">
        <i class="fa fa-map-marked-alt"></i> Route
      </div>
      <div class="card-body">
        <div v-if="hasRouteInfo">
          <div v-if="conversation.context.origin" class="mb-1">
            <div><strong>Origin:</strong></div>
            <div><b>{{ conversation.context.origin }}</b></div>
          </div>
          <div v-if="conversation.context.destination" class="mb-1">
            <div><strong>Destination:</strong></div>
            <div><b>{{ conversation.context.destination }}</b></div>
          </div>
          <div v-if="conversation.context.transportType" class="mb-1">
            <div><strong>Transport:</strong></div>
            <div><b>{{ conversation.context.transportType }}</b></div>
          </div>
          <div v-if="conversation.context.timeline" class="mb-1">
            <div><strong>Timeline:</strong></div>
            <div><b>{{ conversation.context.timeline }}</b></div>
          </div>
          <div v-if="conversation.context.estimatedPrice" class="mt-2 price-estimate">
            <strong>Estimated Price:</strong>
            <div class="price-value">${{ Number(conversation.context.estimatedPrice).toFixed(2) }}</div>
          </div>
        </div>
        <div v-else class="text-muted small">
          <i class="fa fa-info-circle"></i> No route information available
        </div>
      </div>
    </div>

    <div class="card">
      <div class="card-header">
        <i class="fa fa-car"></i> Vehicle
      </div>
      <div class="card-body">
        <div v-if="hasVehicleInfo">
          <div v-if="conversation.context.vehicleYear" class="mb-1">
            <strong>Year:</strong> <b>{{ conversation.context.vehicleYear }}</b>
          </div>
          <div v-if="conversation.context.vehicleMake" class="mb-1">
            <strong>Make:</strong> <b>{{ conversation.context.vehicleMake }}</b>
          </div>
          <div v-if="conversation.context.vehicleModel" class="mb-1">
            <strong>Model:</strong> <b>{{ conversation.context.vehicleModel }}</b>
          </div>
          <div v-if="conversation.context.vehicleType" class="mb-1">
            <strong>Type:</strong> <b>{{ conversation.context.vehicleType }}</b>
          </div>
          <div v-if="conversation.context.vehicleCondition" class="mb-1">
            <strong>Condition:</strong> <b>{{ conversation.context.vehicleCondition }}</b>
          </div>
        </div>
        <div v-else class="text-muted small">
          <i class="fa fa-info-circle"></i> No vehicle information available
        </div>
      </div>
    </div>

    <div class="card">
      <div class="card-header">
        <i class="fa fa-exclamation-circle"></i> Special Requirements
      </div>
      <div v-if="conversation.context.specialRequirements" class="card-body">
        <b>{{ conversation.context.specialRequirements }}</b>
      </div>
      <div v-else class="card-body ">
        <div class="text-muted small">
          <i class="fa fa-info-circle"></i> No Special Requirements
        </div>
      </div>
    </div>

    <div class="card">
      <div class="card-header">
        <i class="fa fa-info"></i> Metadata
      </div>
      <div class="card-body">
        <div v-if="conversation.startedAt" class="mb-2">
          <div><strong>Started:</strong></div>
          <div><b>{{ formatDateTime(conversation.startedAt) }}</b></div>
        </div>
        <div v-if="conversation.takenOverAt" class="mb-2">
          <div><strong>Taken Over:</strong></div>
          <div><b>{{ formatDateTime(conversation.takenOverAt) }}</b></div>
        </div>
        <div v-if="conversation.closedAt" class="mb-2">
          <div><strong>Closed:</strong></div>
          <div><b>{{ formatDateTime(conversation.closedAt) }}</b></div>
        </div>
        <div v-if="conversation.lastActivityAt" class="mb-2">
          <div><strong>Last Activity:</strong></div>
          <div><b>{{ formatDateTime(conversation.lastActivityAt) }}</b></div>
        </div>
        <div v-if="conversation.duration !== null && conversation.duration !== undefined" class="mb-2">
          <div><strong>Duration:</strong></div>
          <div><b>{{ conversation.duration }} minutes</b></div>
        </div>
        <div v-if="conversation.status" class="mb-2">
          <strong>Status:</strong>
          <div>
            <ConversationStatusBadge :status="conversation.status" />
          </div>
        </div>
        <div v-if="conversation.channel" class="mb-2">
          <div><strong>Channel:</strong></div>
          <div><b>{{ conversation.channel }}</b></div>
        </div>
        <div v-if="conversation.agent" class="mb-2">
          <div><strong>Agent:</strong></div>
          <div><b>{{ conversation.agent.name }}</b></div>
        </div>
      </div>
    </div>

    <div class="card">
      <div class="card-header">
        <i class="fa fa-chart-line"></i> Intent Score
      </div>
      <div class="card-body text-center">
        <div class="intent-gauge">
          <div class="gauge-value" :class="intentScoreClass">
            {{ conversation.context.intentScore || 0 }}%
          </div>
          <div class="gauge-bar">
            <div class="gauge-fill" :style="gaugeStyle" :class="intentScoreClass"></div>
          </div>
          <div class="gauge-label">{{ intentLabel }}</div>
        </div>
      </div>
    </div>

    <div class="card">
      <div class="card-header">
        <i class="fa fa-check-circle"></i> Converted to Lead
      </div>
      <div v-if="conversation.leadId" class="card-body d-flex flex-wrap align-content-center">
        <router-link :to="{ name: 'LeadDetails', params: { id: conversation.leadId } }" class="btn btn-success btn-block">
          <i class="fa fa-external-link-alt"></i> View Lead #{{ conversation.leadId }}
        </router-link>
      </div>
      <div v-else class="card-body ">
        <div class="text-muted small">
          <i class="fa fa-info-circle"></i> Not converted to lead
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import moment from 'moment';
import ConversationStatusBadge from '@/components/shared/ConversationStatusBadge';

export default {
  name: 'ContextSidebar',
  components: {
    ConversationStatusBadge,
  },
  props: {
    conversation: {
      type: Object,
      default: () => ({}),
    },
  },
  computed: {
    hasContactInfo() {
      return (
        this.conversation.context.contactName ||
        this.conversation.context.contactEmail ||
        this.conversation.context.contactPhone ||
        this.conversation.context.contactCompany
      );
    },
    hasVehicleInfo() {
      return (
        this.conversation.context.vehicleYear ||
        this.conversation.context.vehicleMake ||
        this.conversation.context.vehicleModel ||
        this.conversation.context.vehicleType ||
        this.conversation.context.vehicleCondition
      );
    },
    hasRouteInfo() {
      return (
        this.conversation.context.origin ||
        this.conversation.context.destination ||
        this.conversation.context.transportType ||
        this.conversation.context.timeline ||
        this.conversation.context.estimatedPrice
      );
    },
    intentScoreClass() {
      const score = this.conversation.context.intentScore || 0;
      if (score >= 70) return 'intent-high';
      if (score >= 50) return 'intent-medium';
      return 'intent-low';
    },
    intentHeaderClass() {
      const score = this.conversation.context.intentScore || 0;
      if (score >= 70) return 'bg-success text-white';
      if (score >= 50) return 'bg-warning';
      return 'bg-danger text-white';
    },
    intentLabel() {
      const score = this.conversation.context.intentScore || 0;
      if (score >= 70) return 'High Intent';
      if (score >= 50) return 'Medium Intent';
      return 'Low Intent';
    },
    gaugeStyle() {
      const width = Math.min(100, Math.max(0, this.conversation.context.intentScore || 0));
      return { width: `${width}%` };
    },
  },
  methods: {
    formatDateTime(dateTime) {
      if (!dateTime) return '';
      return moment(dateTime).format('MMM D, YYYY h:mm A');
    },
  },
};
</script>

<style lang="scss" scoped>
.context-sidebar {
  column-count: 2;
  column-gap: 12px;
}

.context-sidebar .card {
  break-inside: avoid;
  margin-bottom: 12px;
  width: 100%;
}


.context-sidebar .card {
  border-radius: 6px;
}

/* mobilni prikaz */
@media (max-width: 768px) {
  .context-sidebar {
    grid-template-columns: 1fr;
  }
}

.card-header {
  font-weight: 600;
  font-size: 0.8rem;
  padding: 0.35rem 0.5rem;
  background-color: #E2E5EA;
}
.dark-style .card-header {
  background-color: #41434B!important;
}
.card-header i {
  margin-right: 0.4rem;
}

.card-body {
  padding: 0.5rem 0.6rem;
  font-size: 0.8rem;
}

.card-body strong {
  color: #b0b0b0;
  font-size: 0.7rem;
  text-transform: uppercase;
  letter-spacing: 0.3px;
}

.price-value {
  font-size: 1.25rem;
  font-weight: 600;
}

.intent-gauge .gauge-value {
  font-size: 18px;
  font-weight: 700;
}

.intent-high { color: #28a745 !important; }
.intent-medium { color: #ffc107 !important; }
.intent-low { color: #dc3545 !important; }

.gauge-bar {
  width: 100%;
  height: 8px;
  border-radius: 0.5rem;
  overflow: hidden;
  margin-bottom: 0.3rem;
}

.gauge-fill {
  height: 100%;
  transition: width 0.3s ease;
  border-radius: 0.5rem;
}

.gauge-fill.intent-high { background-color: #28a745; }
.gauge-fill.intent-medium { background-color: #ffc107; }
.gauge-fill.intent-low { background-color: #dc3545; }

.gauge-label {
  font-weight: 600;
  text-transform: uppercase;
  font-size: 10px;
  letter-spacing: 0.4px;
}

.btn {
  padding: 0.3rem 0.5rem;
  font-size: 0.8rem;
}

.mb-2 {
  margin-bottom: 0.25rem !important;
}

.text-muted.small {
  color: #999;
}

</style>